if (!customElements.get('td-buy-box')) {
  class TdBuyBox extends HTMLElement {
    constructor() {
      super();
      this.formId = this.getAttribute('form-id');
      this.labels = { add: this.dataset.labelAdd, preorder: this.dataset.labelPreorder };
      const data = this.querySelector('script[type="application/json"]');
      this.variants = data ? JSON.parse(data.textContent) : {};
      this.accessoriesTotal = 0;
      this.currentVariant = null;
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onAccessoriesChanged = this.onAccessoriesChanged.bind(this);
    }

    connectedCallback() {
      if (window.themeVariables && window.themeVariables.settings) {
        window.themeVariables.settings.currencyCodeEnabled = false;
      }
      document.addEventListener('variant:changed', this.onVariantChanged);
      document.addEventListener('td-accessories:changed', this.onAccessoriesChanged);
      document.querySelectorAll('product-variants').forEach((el) => el.classList.add('variants-enabled'));
      const form = document.getElementById(this.formId);
      const idInput = form ? form.querySelector('input[name="id"]') : null;
      const button = form ? form.querySelector('[data-product-add-to-cart-button]') : null;
      if (idInput) {
        this.currentVariant = { id: parseInt(idInput.value, 10), available: button ? !button.classList.contains('button--ternary') : true };
      }
      this.initStickyColumns();
      this.initStyluxPlacement();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
      document.removeEventListener('td-accessories:changed', this.onAccessoriesChanged);
      if (this.columnObserver) this.columnObserver.disconnect();
      if (this.onStickyScroll) window.removeEventListener('scroll', this.onStickyScroll);
      if (this.onColumnsResize) window.removeEventListener('resize', this.onColumnsResize);
      if (this.applyStickySoon) {
        if (this.stickyClickTarget) this.stickyClickTarget.removeEventListener('click', this.applyStickySoon);
        document.removeEventListener('variant:changed', this.applyStickySoon);
      }
      if (this.applyTimer) clearTimeout(this.applyTimer);
      if (this.styluxObserver) this.styluxObserver.disconnect();
    }

    initStyluxPlacement() {
      const form = document.querySelector('.td-main-product .product-form');
      if (!form) return;
      const place = () => {
        const container = document.getElementById('stylux-offer-container');
        const buyButtons = form.querySelector('.product-form__buy-buttons');
        if (!container || !buyButtons || container.parentElement === buyButtons) return;
        buyButtons.prepend(container);
      };
      place();
      this.styluxObserver = new MutationObserver(place);
      this.styluxObserver.observe(form, { childList: true, subtree: true });
    }

    initStickyColumns() {
      const wrapper = document.querySelector('.td-main-product .product');
      if (!wrapper) return;
      const gallery = wrapper.querySelector('.product__media');
      const info = wrapper.querySelector('.product__info');
      if (!gallery || !info) return;
      const apply = () => {
        const desktop = matchMedia('(min-width: 1000px)').matches;
        const prev = this.stickyState;
        this.stickyState = null;
        [gallery, info].forEach((el) => {
          el.classList.remove('td-pdp-sticky-col');
          el.style.removeProperty('--td-sticky-top');
        });
        if (!desktop) return;
        const headerEl = document.querySelector('#shopify-section-header header') || document.querySelector('header');
        const headerClearance = headerEl ? headerEl.getBoundingClientRect().height + 16 : 0;
        const anchor = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--anchor-offset'), 10) || 0;
        const offset = Math.max(anchor, headerClearance, 24);
        const shorter = gallery.offsetHeight <= info.offsetHeight ? gallery : info;
        const stickyGap = 40;
        const fits = shorter.offsetHeight <= window.innerHeight - offset - stickyGap;
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const wrapperBottom = wrapper.getBoundingClientRect().bottom + window.scrollY;
        const endAlignedTop = wrapperBottom - maxScroll - shorter.offsetHeight;
        let top;
        if (fits) {
          top = offset + stickyGap;
        } else {
          const lower = Math.max(window.innerHeight - shorter.offsetHeight - 24, endAlignedTop);
          const upper = Math.max(offset + stickyGap, lower);
          if (prev && prev.el === shorter) {
            top = prev.top;
          } else {
            top = lower;
            if (top > 0) top = Math.max(top, headerClearance);
          }
          top = Math.min(upper, Math.max(lower, top));
          this.stickyState = { el: shorter, lower, upper, top };
        }
        shorter.classList.add('td-pdp-sticky-col');
        shorter.style.setProperty('--td-sticky-top', `${top}px`);
      };
      const applySoon = () => {
        clearTimeout(this.applyTimer);
        this.applyTimer = setTimeout(apply, 50);
      };
      this.onColumnsResize = apply;
      this.applyStickySoon = applySoon;
      this.stickyClickTarget = wrapper.closest('section, .shopify-section, body');
      this.lastScrollY = window.scrollY;
      this.onStickyScroll = () => {
        const y = window.scrollY;
        const dy = y - this.lastScrollY;
        this.lastScrollY = y;
        const state = this.stickyState;
        if (!state || !dy) return;
        const next = Math.min(state.upper, Math.max(state.lower, state.top - dy));
        if (next === state.top) return;
        state.top = next;
        state.el.style.setProperty('--td-sticky-top', `${next}px`);
      };
      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      this.columnObserver = new ResizeObserver(applySoon);
      this.columnObserver.observe(gallery);
      this.columnObserver.observe(info);
      window.addEventListener('resize', this.onColumnsResize);
      this.stickyClickTarget.addEventListener('click', this.applyStickySoon);
      document.addEventListener('variant:changed', this.applyStickySoon);
      apply();
    }

    onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !this.variants[variant.id]) return;
      this.currentVariant = variant;
      this.updateStockMessage(variant);
      this.updateButton(variant);
      this.updateStickyPrice(variant);
      this.updatePriceList(variant);
    }

    onAccessoriesChanged(event) {
      if (!event.detail || event.detail.formId !== this.formId) return;
      this.accessoriesTotal = event.detail.total || 0;
      if (this.currentVariant && this.variants[this.currentVariant.id]) this.updateButton(this.currentVariant);
    }

    buttonPrice(entry) {
      if (!this.accessoriesTotal || entry.cents == null) return entry.price;
      const format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}';
      const placeholder = format.match(/{{\s*(\w+)\s*}}/);
      const key = placeholder ? placeholder[1] : 'amount';
      const value = (entry.cents + this.accessoriesTotal) / 100;
      let amount = key.includes('no_decimals')
        ? Math.round(value).toString()
        : Number.isInteger(value) ? value.toString() : value.toFixed(2);
      if (key.includes('comma_separator')) amount = amount.replace('.', ',');
      return placeholder ? format.replace(placeholder[0], amount) : format + amount;
    }

    updatePriceList(variant) {
      const entry = this.variants[variant.id];
      const meta = document.querySelector(`product-meta[form-id="${this.formId}"]`);
      const list = meta ? meta.querySelector('[data-product-price-list]') : null;
      if (!list) return;
      const setText = (el, value) => {
        if (!el || !value) return;
        if (el.lastChild && el.lastChild.nodeType === Node.TEXT_NODE) el.lastChild.textContent = value;
        else el.append(value);
      };
      const currents = list.querySelectorAll('[data-price-current]');
      if (currents.length) {
        currents.forEach((el) => { el.textContent = entry.price; });
        if (entry.compare) list.querySelectorAll('[data-price-compare]').forEach((el) => { el.textContent = entry.compare; });
      } else {
        setText(list.querySelector('.price:not(.price--compare):not(.text--subdued)'), entry.price);
        setText(list.querySelector('.price--compare'), entry.compare);
      }
    }

    updateStickyPrice(variant) {
      const price = document.querySelector('.product-sticky-form__price');
      if (price) price.textContent = this.variants[variant.id].price;
    }

    updateStockMessage(variant) {
      document.querySelectorAll('.stock-message .ism-holder').forEach((el) => {
        el.style.display = 'none';
      });
      const current = document.getElementById(`ism-${variant.id}`);
      if (current) current.style.display = '';
    }

    updateButton(variant) {
      const entry = this.variants[variant.id];
      const form = document.getElementById(this.formId);
      const button = form ? form.querySelector('[data-product-add-to-cart-button]') : null;
      if (!button) return;
      const label = entry.preorder ? this.labels.preorder : this.labels.add;
      const content = `${label} - ${this.buttonPrice(entry)}`;
      button.setAttribute('data-button-content', content);
      if (this.settleTimer) clearTimeout(this.settleTimer);
      if (this.observer) this.observer.disconnect();
      if (!variant.available) return;
      const target = button.getAttribute('is') === 'loader-button' && button.firstElementChild ? button.firstElementChild : button;
      const apply = () => {
        if (target.textContent.trim() !== content) target.textContent = content;
      };
      apply();
      this.observer = new MutationObserver(apply);
      this.observer.observe(button, { childList: true, characterData: true, subtree: true });
      this.settleTimer = setTimeout(() => this.observer.disconnect(), 1000);
    }
  }

  customElements.define('td-buy-box', TdBuyBox);
}
